import { NgClass } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { L10n, L10nBase } from '@skylab-angular-shared/core';

@Component({
    selector: 'skl-pagination',
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss'],
    imports: [NgClass]
})
export class PaginationComponent implements OnInit, OnChanges {

    @Input() public currentPage!: number;

    @Input() public totalAmount!: number;

    @Input() public options!: PaginationOptions;

    @Input() public getPageUrl!: (page: number) => string;

    @Output() public pageChange = new EventEmitter<number>();

    public buttonFirst!: IPaginationButton;
    public buttonNext!: IPaginationButton;
    public buttonPrevious!: IPaginationButton;
    public buttonLast!: IPaginationButton;
    public buttonsNumeric!: number[];

    public constructor(
        @Inject(L10n) private l10n: L10nBase,
    ) { }

    public ngOnInit(): void {
        this.buttonFirst = {
            name: this.l10n._T('ui.pagination.buttons.first'),
            isDisabled: () => this.currentPage === 1,
        };

        this.buttonNext = {
            name: this.l10n._T('ui.pagination.buttons.next'),
            isDisabled: () => this.currentPage === this.getPagesAmount(),
        };

        this.buttonPrevious = {
            name: this.l10n._T('ui.pagination.buttons.previous'),
            isDisabled: () => this.currentPage === 1,
        };

        this.buttonLast = {
            name: this.l10n._T('ui.pagination.buttons.last'),
            isDisabled: () => this.currentPage === this.getPagesAmount(),
        };
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj['currentPage'] && changesObj['currentPage'].currentValue) {
            this.recalculateNumericButtons();
        }

        if (changesObj['totalAmount'] && changesObj['totalAmount'].currentValue) {
            this.recalculateNumericButtons();
        }
    }

    private recalculateNumericButtons(): void {
        let range = this.options.range;
        if (this.getPagesAmount() < range)
            range = this.getPagesAmount();

        this.buttonsNumeric = this.setPages(range);
    }

    private setPages(range: number): number[] {
        const rangeCap = Math.floor(range / 2);
        const pages = [];
        const currentMaxPageDiffrence = this.getPagesAmount() - this.currentPage;
        const initialIteratorValue = ((this.currentPage - rangeCap > 0) ?
            ((currentMaxPageDiffrence <= rangeCap) ?
                (this.currentPage - (range - 1 - currentMaxPageDiffrence))
                : this.currentPage - rangeCap)
            : 1);
        for (let i = initialIteratorValue; i <= initialIteratorValue + range - 1; i++) {
            pages.push(i);
        }
        return pages;
    }

    public getPagesAmount(): number {
        return Math.ceil(this.totalAmount / this.options.limit);
    }

    public isPaginationVisible(): boolean {
        return this.totalAmount > this.options.limit;
    }

    public previousPage(): number {
        return this.currentPage - 1 || 1;
    }

    public nextPage(): number {
        return this.maxPage(this.currentPage + 1);
    }

    public lastPage(): number {
        return this.maxPage(this.getPagesAmount());
    }

    public maxPage(page: number): number {
        const pagesAmount = this.getPagesAmount();

        return page <= pagesAmount ? page : pagesAmount;
    }

    public changePage(pageNumber: number, isDisabled: boolean, event: MouseEvent): void {
        if (isDisabled || (this.currentPage === pageNumber)) {
            event.preventDefault();
            return;
        }
        if (event.button == 4) {
            return;
        }
        this.currentPage = pageNumber;
        this.recalculateNumericButtons();
        this.pageChange.emit(this.currentPage);
        event.preventDefault();
    }

    public getButtonClassNames(button: IPaginationButton): string {
        if (button.isDisabled()) {
            return 'pagination-button--disabled';
        }
        return '';
    }

    public getNumericButtonClassNames(page: number): string {
        if (this.currentPage === page) {
            return 'pagination-button--active';
        }
        return '';
    }
}

export interface PaginationOptions {
    limit: number;
    range: number;
}

interface IPaginationButton {
    name: string;
    isDisabled: () => boolean;
}
